import { AlertCircle, Check } from "lucide-react";

/**
 * Tabla de asignación calendario ↔ técnico. Permite seleccionar un calendario
 * de Google por técnico (sin duplicados) y asignarle un color personalizado.
 */
export default function TechnicianCalendarTable({
  technicians,
  calendars,
  disabled,
  savingRow,
  onAssign,
  onUpdateColor,
}) {
  // Calendarios ya asignados (para evitar duplicados)
  const assignedCalIds = new Set(
    technicians.filter((t) => t.google_calendar_id).map((t) => t.google_calendar_id)
  );

  return (
    <div className="ds-card p-5 space-y-4">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#6E8B3D]">
          // Asignación
        </div>
        <h3 className="font-bold text-lg mt-1">Calendarios por técnico</h3>
        <p className="text-xs text-[#525252] mt-1">
          Selecciona qué calendario de Google usará cada técnico. No se puede asignar el mismo
          calendario a dos técnicos distintos.
        </p>
      </div>

      {technicians.length === 0 ? (
        <div className="text-sm text-[#525252] italic flex items-center gap-2">
          <AlertCircle size={14} /> No hay técnicos creados. Ve a Usuarios → Nuevo y asigna el rol
          &quot;technician&quot;.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#F7F7F4] border-b border-[#E5E5E0]">
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                  Técnico
                </th>
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                  Calendario asignado
                </th>
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                  ID
                </th>
                <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                  Color
                </th>
              </tr>
            </thead>
            <tbody>
              {technicians.map((t) => (
                <tr key={t.id} className="border-b border-[#E5E5E0]">
                  <td className="p-3">
                    <div className="font-medium">{t.name}</div>
                    <div className="text-[11px] text-[#525252]">{t.email}</div>
                  </td>
                  <td className="p-3">
                    <select
                      value={t.google_calendar_id || ""}
                      onChange={(e) => {
                        const sel = calendars.find((c) => c.id === e.target.value);
                        onAssign(t.id, e.target.value || "", sel?.summary || "");
                      }}
                      disabled={disabled || savingRow === t.id}
                      data-testid={`google-tech-cal-${t.id}`}
                      className="w-full ds-card px-2 py-1.5 text-sm outline-none focus:border-[#6E8B3D]"
                    >
                      <option value="">— Sin calendario —</option>
                      {calendars.map((c) => {
                        const disabledOpt =
                          assignedCalIds.has(c.id) && c.id !== t.google_calendar_id;
                        return (
                          <option key={c.id} value={c.id} disabled={disabledOpt}>
                            {c.summary}
                            {c.primary ? " (principal)" : ""}
                            {disabledOpt ? " · ya asignado" : ""}
                          </option>
                        );
                      })}
                    </select>
                  </td>
                  <td
                    className="p-3 text-[11px] font-mono text-[#525252] truncate max-w-[200px]"
                    title={t.google_calendar_id}
                  >
                    {t.google_calendar_id || "—"}
                  </td>
                  <td className="p-3">
                    <input
                      type="color"
                      value={t.google_calendar_color || "#6E8B3D"}
                      disabled={!t.google_calendar_id || disabled}
                      onChange={(e) =>
                        onUpdateColor(
                          t.id,
                          t.google_calendar_id,
                          t.google_calendar_name,
                          e.target.value
                        )
                      }
                      className="w-10 h-8 border border-[#E5E5E0] cursor-pointer disabled:opacity-30"
                      data-testid={`google-tech-color-${t.id}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-xs text-[#525252] flex items-start gap-2 pt-1">
        <Check size={12} className="text-[#6E8B3D] mt-0.5" />
        Calendarios encontrados: <strong>{calendars.length}</strong>. Las reparaciones con Técnico
        + Fecha prevista se sincronizan automáticamente.
      </div>
    </div>
  );
}
